import APP_CONFIG from '@/config/app';
import AuthorizationService from '@/services/AuthorizationService';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { API_ERRORS } from '@/utils/strings';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { Auth, getAuth, User } from 'firebase/auth';
const apiClient: AxiosInstance = axios.create({
  baseURL: APP_CONFIG.API_SERVICE.BASE_URL,
});
const MODULE_NAME = 'ApiService';
let tokenRefreshInterval: NodeJS.Timeout | null = null;
const refreshToken = async (): Promise<string> => {
  const auth: Auth = getAuth();
  const currentUser: User | null = auth.currentUser;
  if (!currentUser) {
    logError(
      'No current user found for token refresh.',
      `${MODULE_NAME}.refreshToken`,
    );
    throw new Error('No current user to refresh token for');
  }
  try {
    const token: string = await currentUser.getIdToken(true);
    sessionStorage.setItem('authToken', token);
    logDebug(
      'Token refreshed and stored in session',
      `${MODULE_NAME}.refreshToken`,
    );
    return token;
  } catch (error) {
    logError(error, `${MODULE_NAME}.refreshToken`, { userId: currentUser.uid });
    throw error;
  }
};
export const startTokenRefreshInterval = (): void => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    logDebug(
      'Cleared existing token refresh interval',
      `${MODULE_NAME}.startTokenRefreshInterval`,
    );
  }
  logDebug(
    'Starting token refresh interval',
    `${MODULE_NAME}.startTokenRefreshInterval`,
    {
      intervalMs: APP_CONFIG.API_SERVICE.TOKEN_REFRESH_INTERVAL_MS,
    },
  );
  tokenRefreshInterval = setInterval(async () => {
    const auth: Auth = getAuth();
    if (auth.currentUser) {
      try {
        await refreshToken();
        logInfo(
          'Token refreshed proactively via interval',
          `${MODULE_NAME}.tokenRefreshInterval`,
        );
      } catch (error) {
        logError(error, `${MODULE_NAME}.tokenRefreshInterval`, {
          message: 'Proactive refresh failed',
        });
      }
    } else {
      logDebug(
        'No user logged in, skipping proactive token refresh',
        `${MODULE_NAME}.tokenRefreshInterval`,
      );
    }
  }, APP_CONFIG.API_SERVICE.TOKEN_REFRESH_INTERVAL_MS);
};
export const stopTokenRefreshInterval = (): void => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
    logDebug(
      'Token refresh interval stopped',
      `${MODULE_NAME}.stopTokenRefreshInterval`,
    );
  }
};
apiClient.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const auth: Auth = getAuth();
    const currentUser: User | null = auth.currentUser;
    if (!currentUser) {
      logDebug(
        'No user logged in, proceeding without auth token',
        `${MODULE_NAME}.requestInterceptor`,
        { url: config.url },
      );
      return config;
    }
    try {
      const token: string = await currentUser.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        logDebug(
          'Added auth token to request header',
          `${MODULE_NAME}.requestInterceptor`,
          { url: config.url },
        );
      } else {
        logDebug(
          'No token available for current user',
          `${MODULE_NAME}.requestInterceptor`,
          { url: config.url },
        );
      }
    } catch (error) {
      logError(error, `${MODULE_NAME}.requestInterceptor`, {
        message: 'Failed to get ID token for request',
        url: config.url,
      });
    }
    return config;
  },
  (error) => {
    logError(error, `${MODULE_NAME}.requestInterceptor`, {
      message: 'Request interceptor error',
    });
    return Promise.reject(error);
  },
);
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError | any) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    if (isBetaRestrictionError(error)) {
      logInfo(
        'User not authorized for beta access',
        `${MODULE_NAME}.responseInterceptor`,
        {
          url: originalRequest?.url,
          errorData: error.response?.data,
        },
      );
      AuthorizationService.setUnauthorized();
      return Promise.reject({ ...error, isBetaRestricted: true });
    }
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      logDebug(
        'Received 401, attempting token refresh',
        `${MODULE_NAME}.responseInterceptor`,
        { url: originalRequest.url },
      );
      const auth: Auth = getAuth();
      if (!auth.currentUser) {
        logError(
          'User session expired or user logged out during request',
          `${MODULE_NAME}.responseInterceptor`,
          { url: originalRequest.url },
        );
        sessionStorage.removeItem('authToken');
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.reload();
          }, APP_CONFIG.API_SERVICE.SESSION_RELOAD_DELAY_MS);
        }
        return Promise.reject(
          new Error(API_ERRORS.SESSION_EXPIRED_ERROR_DESCRIPTION),
        );
      }
      try {
        const newToken = await refreshToken();
        logInfo(
          'Token refreshed successfully after 401, retrying request',
          `${MODULE_NAME}.responseInterceptor`,
          { url: originalRequest.url },
        );
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        logError(refreshError, `${MODULE_NAME}.responseInterceptor`, {
          message: 'Token refresh failed after 401',
          url: originalRequest.url,
        });
        sessionStorage.removeItem('authToken');
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);
interface ApiErrorData {
  errorCode?: string;
  errorMessage?: string;
}
const isAxiosErrorWithResponseData = (
  error: unknown,
): error is AxiosError<ApiErrorData> => {
  return (
    axios.isAxiosError(error) &&
    typeof error.response?.data === 'object' &&
    error.response.data !== null
  );
};
const isBetaRestrictionError = (error: unknown): boolean => {
  return (
    isAxiosErrorWithResponseData(error) &&
    error.response?.status === 401 &&
    error.response?.data?.errorCode === 'UNAUTHORIZED' &&
    error.response?.data?.errorMessage === 'Not part of beta program'
  );
};
const isServerError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 500;
};
const isNetworkError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && !!error.request && !error.response;
};
const isTimeoutError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED';
};
const isForbiddenError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 403;
};
const isNotFoundError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 404;
};
const isRateLimitError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 429;
};
const isCorsError = (error: unknown): boolean => {
  return (
    axios.isAxiosError(error) &&
    typeof error.message === 'string' &&
    (error.message.toLowerCase().includes('cors') ||
      error.message.toLowerCase().includes('cross-origin'))
  );
};
const isParseError = (error: unknown): boolean => {
  return (
    axios.isAxiosError(error) &&
    typeof error.message === 'string' &&
    (error.message.toLowerCase().includes('json') ||
      error.message.toLowerCase().includes('parse'))
  );
};
const isFirebaseAuthError = (error: unknown): boolean => {
  return (
    error instanceof Error &&
    error.name === 'FirebaseError' &&
    typeof (error as any).code === 'string' &&
    (error as any).code.startsWith('auth/')
  );
};
interface StandardApiErrorResponse {
  error: true;
  errorCode: string;
  errorMessage: string;
  title: string;
  description: string;
  isBetaRestricted?: boolean;
  isServerError?: boolean;
  isNetworkError?: boolean;
  isTimeoutError?: boolean;
  isForbiddenError?: boolean;
  isNotFoundError?: boolean;
  isRateLimitError?: boolean;
  isCorsError?: boolean;
  isParseError?: boolean;
  isAuthError?: boolean;
}
interface BetaRestrictedResponse {
  isBetaRestricted: true;
}
type ApiErrorResponse<T> =
  | StandardApiErrorResponse
  | BetaRestrictedResponse
  | T;
const getErrorDetails = (
  error: unknown,
): { errorCode: string; errorMessage: string } => {
  const defaultError = {
    errorCode: 'UNKNOWN_ERROR',
    errorMessage: API_ERRORS.SERVER_ERROR_DESCRIPTION,
  };
  if (isAxiosErrorWithResponseData(error)) {
    return {
      errorCode: error.response!.data.errorCode || 'SERVER_ERROR',
      errorMessage:
        error.response!.data.errorMessage ||
        API_ERRORS.SERVER_ERROR_DESCRIPTION,
    };
  } else if (error instanceof Error) {
    return {
      errorCode: 'CLIENT_ERROR',
      errorMessage: error.message,
    };
  }
  return defaultError;
};
interface ErrorHandler<T> {
  predicate: (error: unknown) => boolean;
  handle: (
    error: unknown,
    context: { method: string; url: string; data?: any },
  ) => ApiErrorResponse<T>;
}
const handleApiError = <T>(
  error: unknown,
  method: string,
  url: string,
  data?: any,
): ApiErrorResponse<T> => {
  const context = { method: method.toUpperCase(), url, data };
  const logContext = { url, ...(data && { data }) };
  const errorLogger = (type: string, err: unknown, details?: object) =>
    logError(err, `${MODULE_NAME}.${context.method.toLowerCase()}.${type}`, {
      ...logContext,
      ...details,
    });
  const errorHandlers: Array<ErrorHandler<T>> = [
    {
      predicate: (err) =>
        (err as any)?.isBetaRestricted === true || isBetaRestrictionError(err),
      handle: (err) => {
        errorLogger('betaRestriction', err);
        AuthorizationService.setUnauthorized();
        return { isBetaRestricted: true } as ApiErrorResponse<T>;
      },
    },
    {
      predicate: isServerError,
      handle: (err) => {
        const { errorCode, errorMessage } = getErrorDetails(err);
        errorLogger('serverError', err, { errorCode, errorMessage });
        return {
          error: true,
          errorCode,
          errorMessage,
          isServerError: true,
          title: API_ERRORS.SERVER_ERROR_TITLE,
          description: API_ERRORS.SERVER_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isNetworkError,
      handle: (err) => {
        errorLogger('networkError', err);
        return {
          error: true,
          errorCode: 'NETWORK_ERROR',
          errorMessage: 'Unable to connect to the server',
          isNetworkError: true,
          title: API_ERRORS.NETWORK_ERROR_TITLE,
          description: API_ERRORS.NETWORK_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isTimeoutError,
      handle: (err) => {
        errorLogger('timeoutError', err);
        return {
          error: true,
          errorCode: 'TIMEOUT_ERROR',
          errorMessage: 'Request timed out',
          isTimeoutError: true,
          title: API_ERRORS.TIMEOUT_ERROR_TITLE,
          description: API_ERRORS.TIMEOUT_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isForbiddenError,
      handle: (err) => {
        errorLogger('forbiddenError', err);
        return {
          error: true,
          errorCode: 'FORBIDDEN_ERROR',
          errorMessage: 'Access denied',
          isForbiddenError: true,
          title: API_ERRORS.FORBIDDEN_ERROR_TITLE,
          description: API_ERRORS.FORBIDDEN_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isNotFoundError,
      handle: (err) => {
        errorLogger('notFoundError', err);
        return {
          error: true,
          errorCode: 'NOT_FOUND_ERROR',
          errorMessage: 'Resource not found',
          isNotFoundError: true,
          title: API_ERRORS.NOT_FOUND_ERROR_TITLE,
          description: API_ERRORS.NOT_FOUND_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isRateLimitError,
      handle: (err) => {
        errorLogger('rateLimitError', err);
        return {
          error: true,
          errorCode: 'RATE_LIMIT_ERROR',
          errorMessage: 'Too many requests',
          isRateLimitError: true,
          title: API_ERRORS.RATE_LIMIT_ERROR_TITLE,
          description: API_ERRORS.RATE_LIMIT_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isCorsError,
      handle: (err) => {
        errorLogger('corsError', err);
        return {
          error: true,
          errorCode: 'CORS_ERROR',
          errorMessage: 'Cross-origin request blocked',
          isCorsError: true,
          title: API_ERRORS.CORS_ERROR_TITLE,
          description: API_ERRORS.CORS_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isParseError,
      handle: (err) => {
        errorLogger('parseError', err);
        return {
          error: true,
          errorCode: 'PARSE_ERROR',
          errorMessage: 'Unable to parse server response',
          isParseError: true,
          title: API_ERRORS.PARSE_ERROR_TITLE,
          description: API_ERRORS.PARSE_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: isFirebaseAuthError,
      handle: (err) => {
        const firebaseError = err as any;
        errorLogger('firebaseAuthError', err, {
          firebaseErrorCode: firebaseError.code,
        });
        return {
          error: true,
          errorCode: 'AUTH_ERROR',
          errorMessage: firebaseError.message || 'Authentication error',
          isAuthError: true,
          title: API_ERRORS.AUTH_ERROR_TITLE,
          description: API_ERRORS.AUTH_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
    {
      predicate: (err) =>
        err instanceof Error &&
        err.message === API_ERRORS.SESSION_EXPIRED_ERROR_DESCRIPTION,
      handle: (err) => {
        errorLogger('sessionExpiredError', err);
        return {
          error: true,
          errorCode: 'SESSION_EXPIRED',
          errorMessage: API_ERRORS.SESSION_EXPIRED_ERROR_DESCRIPTION,
          isAuthError: true,
          title: API_ERRORS.AUTH_ERROR_TITLE,
          description: API_ERRORS.AUTH_ERROR_DESCRIPTION,
        } as StandardApiErrorResponse;
      },
    },
  ];
  for (const handler of errorHandlers) {
    if (handler.predicate(error)) {
      return handler.handle(error, context);
    }
  }
  errorLogger('unhandledError', error);
  throw error;
};
const makeRequest = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any,
): Promise<ApiErrorResponse<T>> => {
  const operationName = `${MODULE_NAME}.${method.toLowerCase()}`;
  const logContext = { url, ...(data && { data }) };
  logDebug(
    `makeRequest ${method} starting`,
    `${operationName}.makeRequest`,
    logContext,
  );
  if (
    !AuthorizationService.getAuthorizationStatus() &&
    !url.includes('/api/v2/user/referral')
  ) {
    logInfo(
      `Skipping ${method} API call due to unauthorized status`,
      operationName,
      logContext,
    );
    logDebug(
      `makeRequest ${method} skipped due to unauthorized status`,
      `${operationName}.makeRequest`,
      logContext,
    );
    return { isBetaRestricted: true } as ApiErrorResponse<T>;
  }
  if (url.includes('/api/v2/user/referral')) {
    logInfo(
      `Proceeding with ${method} API call to referral endpoint even if unauthorized`,
      operationName,
      logContext,
    );
  }
  try {
    logDebug(
      `About to make ${method} request to ${url}`,
      `${operationName}.makeRequest`,
      logContext,
    );
    let response: AxiosResponse<T>;
    const config = data ? { data } : {};
    switch (method) {
      case 'GET':
        logDebug(
          `Executing GET request`,
          `${operationName}.makeRequest.get`,
          logContext,
        );
        response = await apiClient.get<T>(url);
        break;
      case 'POST':
        logDebug(
          `Executing POST request`,
          `${operationName}.makeRequest.post`,
          { url, data },
        );
        response = await apiClient.post<T>(url, data);
        break;
      case 'PUT':
        logDebug(
          `Executing PUT request`,
          `${operationName}.makeRequest.put`,
          logContext,
        );
        response = await apiClient.put<T>(url, data);
        break;
      case 'DELETE':
        logDebug(
          `Executing DELETE request`,
          `${operationName}.makeRequest.delete`,
          logContext,
        );
        response = await apiClient.delete<T>(url, config);
        break;
    }
    logInfo(`${method} request successful`, operationName, {
      ...logContext,
      responseData: response.data,
    });
    logDebug(
      `${method} request completed successfully`,
      `${operationName}.makeRequest`,
      {
        url,
        responseStatus: response.status,
        responseData: response.data,
      },
    );
    return response.data;
  } catch (error: unknown) {
    logDebug(`${method} request failed`, `${operationName}.makeRequest`, {
      url,
      error: error instanceof Error ? error.message : error,
    });
    return handleApiError<T>(error, method, url, data);
  }
};
export const get = async <T = any>(
  url: string,
): Promise<ApiErrorResponse<T>> => {
  return makeRequest<T>('GET', url);
};
export const post = async <T = any>(
  url: string,
  data: any,
): Promise<ApiErrorResponse<T>> => {
  logDebug('ApiService.post called', 'ApiService.post', { url, data });
  try {
    const result = await makeRequest<T>('POST', url, data);
    logDebug('ApiService.post result', 'ApiService.post', {
      url,
      data,
      result,
    });
    return result;
  } catch (error) {
    logError(error, 'ApiService.post', { url, data });
    throw error;
  }
};
export const put = async <T = any>(
  url: string,
  data: any,
): Promise<ApiErrorResponse<T>> => {
  return makeRequest<T>('PUT', url, data);
};
export const deleteApi = async <T = any>(
  url: string,
): Promise<ApiErrorResponse<T>> => {
  return makeRequest<T>('DELETE', url);
};
const initializeApiService = (): void => {
  if (typeof window === 'undefined') {
    logDebug(
      'Not in browser environment, skipping initialization',
      MODULE_NAME,
    );
    return;
  }
  logDebug('Initializing ApiService in browser environment', MODULE_NAME);
  startTokenRefreshInterval();
  if (APP_CONFIG.API_SERVICE.VISIBILITY_CHANGE_TOKEN_REFRESH_ENABLED) {
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible') {
        const auth = getAuth();
        if (auth.currentUser) {
          try {
            await refreshToken();
            logInfo(
              'Token refreshed successfully on visibility change',
              `${MODULE_NAME}.visibilityChange`,
            );
          } catch (error) {
            logError(error, `${MODULE_NAME}.visibilityChange`, {
              message: 'Token refresh failed on visibility change',
            });
          }
        } else {
          logDebug(
            'No user logged in, skipping visibility change token refresh',
            `${MODULE_NAME}.visibilityChange`,
          );
        }
      }
    });
    logDebug('Visibility change listener initialized', MODULE_NAME);
  }
};
initializeApiService();
export const isUserAuthorizedForBeta = (): boolean => {
  return AuthorizationService.getAuthorizationStatus();
};
export const handleUnauthorized = (): void => {
  logInfo(
    'Explicitly setting user as unauthorized for beta access',
    `${MODULE_NAME}.handleUnauthorized`,
  );
  AuthorizationService.setUnauthorized();
};
const ApiService = {
  get,
  post,
  put,
  del: deleteApi,
  startTokenRefreshInterval,
  stopTokenRefreshInterval,
  handleUnauthorized,
  isUserAuthorizedForBeta,
};
export default ApiService;
